<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import { settingsStore } from '../stores/settings';
  import { actionFlags, actionFlagHelpers } from '../stores/actionFlags';
  import { showToast } from '../lib/toast';
  import * as apiService from '../lib/apiService';
  import type { Club } from '../types';
  import ClubForm from '../components/ClubForm.svelte';

  // @ts-ignore - Vite-specific type
  const BUILD_TIMESTAMP = import.meta.env.VITE_BUILD_TIMESTAMP || Date.now();

  type Tab = 'hockey' | 'football' | 'no_logo';
  let activeTab: Tab = 'hockey';
  let clubs: Club[] = [];
  let showClubForm = false;
  let selectedClub: Partial<Club> | null = null;
  let isEditMode = false;
  let searchText = '';

  // Check if user is superuser
  $: isSuperuser = $settingsStore.user?.is_superuser || false;

  // Filter clubs by active tab and search
  $: filteredClubs = clubs
    .filter(club => {
      // Filter by tab
      if (activeTab === 'no_logo') {
        // No logo tab: show clubs without logos (any sport)
        return !club.logo_filename || club.logo_filename === 'default.png';
      } else {
        // Sport tabs: filter by sport
        return club.sport === activeTab;
      }
    })
    .filter(club => {
      // Skip search filter for 'no_logo' tab
      if (activeTab === 'no_logo') {
        return true;
      }
      // Only apply search filter if at least 2 characters entered
      if (searchText.length < 2) {
        return false; // Show all clubs
      }
      const searchLower = searchText.toLowerCase();
      return (
        club.name.toLowerCase().includes(searchLower) ||
        club.location_name?.toLowerCase().includes(searchLower) ||
        club.location_city?.toLowerCase().includes(searchLower)
      );
    });

  // Reactive statement: Load clubs when superuser status is confirmed
  $: if (isSuperuser && clubs.length === 0 && !$actionFlags.isLoadingAllClubs) {
    loadClubs();
  }

  async function loadClubs() {
    if ($actionFlags.isLoadingAllClubs) return;

    actionFlagHelpers.setFlag('isLoadingAllClubs');
    try {
      await apiService.getAllClubs((result, status) => {
        if (status === 200) {
          clubs = result;
        } else {
          showToast($_('clubSettings.errors.loadFailed'), 'error');
        }
      });
    } catch (error) {
      console.error('Error loading clubs:', error);
      showToast($_('clubSettings.errors.loadFailed'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isLoadingAllClubs');
    }
  }

  function setActiveTab(tab: Tab) {
    activeTab = tab;
    // Clear search text when switching tabs
    searchText = '';
  }

  function openCreateForm() {
    selectedClub = {
      sport: activeTab === 'no_logo' ? 'hockey' : activeTab,
      logo_filename: 'default.png'
    };
    isEditMode = false;
    showClubForm = true;
  }

  function openEditForm(club: Club) {
    selectedClub = { ...club };
    isEditMode = true;
    showClubForm = true;
  }

  function closeForm() {
    showClubForm = false;
    selectedClub = null;
    isEditMode = false;
  }

  async function handleCreate(event: CustomEvent) {
    const { clubData } = event.detail;

    if ($actionFlags.isCreatingClub) return;

    actionFlagHelpers.setFlag('isCreatingClub');
    try {
      await apiService.createClub(clubData, (result, status) => {
        if (status === 200 || status === 201) {
          showToast($_('clubSettings.success.created'), 'success');
          loadClubs();
          closeForm();
        } else {
          showToast($_('clubSettings.errors.createFailed'), 'error');
        }
      });
    } catch (error) {
      console.error('Error creating club:', error);
      showToast($_('clubSettings.errors.createFailed'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isCreatingClub');
    }
  }

  async function handleUpdate(event: CustomEvent) {
    const { clubId, clubData } = event.detail;

    if ($actionFlags.isUpdatingClub) return;

    actionFlagHelpers.setFlag('isUpdatingClub');
    try {
      await apiService.updateClub(clubId, clubData, (result, status) => {
        if (status === 200) {
          showToast($_('clubSettings.success.updated'), 'success');
          loadClubs();
          closeForm();
        } else {
          showToast($_('clubSettings.errors.updateFailed'), 'error');
        }
      });
    } catch (error) {
      console.error('Error updating club:', error);
      showToast($_('clubSettings.errors.updateFailed'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isUpdatingClub');
    }
  }

  async function handleDelete(event: CustomEvent) {
    const { clubId } = event.detail;

    if ($actionFlags.isDeletingClub) return;

    actionFlagHelpers.setFlag('isDeletingClub');
    try {
      await apiService.deleteClub(clubId, (result, status) => {
        if (status === 200) {
          showToast(result.message || $_('clubSettings.success.deleted'), 'success');
          loadClubs();
          closeForm();
        } else if (status === 400) {
          // Club has associated teams
          showToast(result.message || $_('clubSettings.errors.deleteFailed'), 'error');
        } else {
          showToast($_('clubSettings.errors.deleteFailed'), 'error');
        }
      });
    } catch (error) {
      console.error('Error deleting club:', error);
      showToast($_('clubSettings.errors.deleteFailed'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isDeletingClub');
    }
  }
</script>

{#if !isSuperuser}
  <!-- Show 404 for non-superusers -->
  <div class="not-found-container">
    <article>
      <h1>404</h1>
      <h3>{$_('errors.notFound')}</h3>
      <p>{$_('errors.pageDoesNotExist')}</p>
      <a href="/account/#/settings" role="button">{$_('common.backToSettings')}</a>
    </article>
  </div>
{:else}
  <div class="club-settings-container">
    <header class="page-header">
      <h2>
        {$_('clubSettings.title')}
      </h2>
      <button class="primary" on:click={openCreateForm}>
        <ion-icon name="add-circle-outline"></ion-icon>
        {$_('clubSettings.addClub')}
      </button>
    </header>

    <!-- Tab Navigation -->
    <nav class="tabs">
      <ul>
        <li>
          <a
            role="button"
            class:active={activeTab === 'hockey'}
            on:click|preventDefault={() => setActiveTab('hockey')}
          >
            {$_('sports.field_hockey')}
            <span class="badge">{clubs.filter(c => c.sport === 'hockey').length}</span>
          </a>
        </li>
        <li>
          <a
            role="button"
            class:active={activeTab === 'football'}
            on:click|preventDefault={() => setActiveTab('football')}
          >
            {$_('sports.football')}
            <span class="badge">{clubs.filter(c => c.sport === 'football').length}</span>
          </a>
        </li>
        <li>
          <a
            role="button"
            class:active={activeTab === 'no_logo'}
            on:click|preventDefault={() => setActiveTab('no_logo')}
          >
            {$_('clubSettings.withoutLogo')}
            <span class="badge">{clubs.filter(c => !c.logo_filename || c.logo_filename === 'default.png').length}</span>
          </a>
        </li>
      </ul>
    </nav>

    <!-- Search (hidden on no_logo tab) -->
    {#if activeTab !== 'no_logo'}
      <div class="search-container">
        <input
          type="text"
          placeholder={$_('clubSettings.search')}
          bind:value={searchText}
        />
      </div>
    {/if}

    <!-- Clubs List -->
    <div class="clubs-list">
      {#if $actionFlags.isLoadingAllClubs}
        <article>
          <p>{$_('clubSettings.loading')}</p>
        </article>
      {:else if filteredClubs.length === 0}
        <article>
          <p class="text-center text-muted">
            {searchText
              ? $_('clubSettings.noResults')
              : $_('clubSettings.noClubs')}
          </p>
        </article>
      {:else}
        {#each filteredClubs as club (club.id)}
          <article class="club-card" on:click={() => openEditForm(club)}>
            <div class="club-header">
              <div class="club-info">
                <h4 class="club-name">{club.name}</h4>
                <div class="club-details">
                  {#if club.location_name}
                    <span class="detail">
                      <ion-icon name="location-outline"></ion-icon>
                      {club.location_name}
                    </span>
                  {/if}
                  {#if club.location_city}
                    <span class="detail">
                      <ion-icon name="business-outline"></ion-icon>
                      {club.location_city}
                    </span>
                  {/if}
                </div>
              </div>
            </div>
            <div class="club-actions">
              <ion-icon name="chevron-forward-outline"></ion-icon>
            </div>
          </article>
        {/each}
      {/if}
    </div>
  </div>

  <!-- Club Form Modal -->
  {#if showClubForm}
    <ClubForm
      club={selectedClub}
      {isEditMode}
      on:create={handleCreate}
      on:update={handleUpdate}
      on:delete={handleDelete}
      on:cancel={closeForm}
    />
  {/if}
{/if}

<style>
  .not-found-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80vh;
    padding: 2rem;
  }

  .not-found-container article {
    text-align: center;
    max-width: 500px;
  }

  .not-found-container h1 {
    font-size: 6rem;
    margin: 0;
    color: var(--pico-muted-color);
  }

  .club-settings-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }

  .page-header h2 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
  }

  .tabs {
    margin-bottom: 2rem;
  }

  .tabs ul {
    display: flex;
    gap: 0;
    list-style: none;
    padding: 0;
    margin: 0;
    border-bottom: 2px solid var(--pico-muted-border-color);
  }

  .tabs li {
    margin: 0;
  }

  .tabs a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    border: none;
    border-bottom: 3px solid transparent;
    background: transparent;
    color: var(--pico-muted-color);
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: -2px;
  }

  .tabs a:hover {
    color: var(--pico-color);
    background-color: var(--pico-secondary-hover-background);
  }

  .tabs a.active {
    color: var(--pico-primary);
    border-bottom-color: var(--pico-primary);
  }

  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.5rem;
    background-color: var(--pico-muted-border-color);
    border-radius: 1rem;
    font-size: 0.75rem;
    font-weight: bold;
  }

  .tabs a.active .badge {
    background-color: var(--pico-primary);
    color: white;
  }

  .search-container {
    margin-bottom: 2rem;
  }

  .clubs-list {
    display: grid;
    gap: 1rem;
  }

  .club-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.2s;
    margin: 0;
  }

  .club-card:hover {
    transform: translateX(4px);
    border-color: var(--pico-primary);
  }

  .club-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  }

  .club-logo {
    width: 60px;
    height: 60px;
    object-fit: contain;
    border-radius: var(--pico-border-radius);
    background-color: white;
    padding: 0.5rem;
  }

  .club-info {
    flex: 1;
  }

  .club-name {
    margin: 0 0 0.5rem 0;
  }

  .club-details {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .detail {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.875rem;
    color: var(--pico-muted-color);
  }

  .detail ion-icon {
    font-size: 1rem;
  }

  .club-actions {
    color: var(--pico-muted-color);
  }

  .club-actions ion-icon {
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }

    .club-header {
      align-items: flex-start;
    }
  }
</style>
